import { Attribute } from '@/common/attribute';
import { decrypt } from '@/common/securityUtils';
import DatePicker from '@/components/forms/DatePicker';
import ImagePreview from '@/components/forms/imagePreview';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import { Upload } from '@/components/upload';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { CalendarDropdownIcon } from '@/icons/calendar-dropdown-icon';
import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { BUSINESS_TYPES } from '../businessType';
import { INDENTIFICATION_TYPES } from '../identificationType';

interface DocumentsProps {
  businessTypeFromApi?: string;
  identificationTypeFromApi?: string;
  identificationTypeOptions?: Attribute[];
  selectedBusinessType: string | undefined;
  passportImagePath?: string | undefined;
  setPassportImage: (image: string) => void;
  kycFrontImage?: string | undefined;
  setKycFrontImage?: (image: string) => void;
  kycBackImage?: string | undefined;
  setKycBackImage?: (image: string) => void;
  insuranceImagePath?: string | undefined;
  setInsuranceImage: (image: string) => void;
  businessImagePath?: string | undefined;
  setBusinessImage: (image: string) => void;
  corporateRegistryImagePath?: string | undefined;
  setCorporateRegistryImage: (image: string) => void;
}

type PremiumStoreDocumentFormValues = {
  identificationType?: string;
  kycFront?: File | string;
  kycBack?: File | string;
  passportImage?: File | string;
  insuranceCardImage?: File | string;
  businessCertificateImage?: File | string;
  corporateRegistryImage?: File | string;
  businessLicenseNumber?: string;
  licenseIssueDate?: string;
};

export const Documents: React.FC<DocumentsProps> = ({
  identificationTypeFromApi,
  selectedBusinessType = '',
  passportImagePath,
  setPassportImage,
  kycFrontImage,
  setKycFrontImage = () => {},
  kycBackImage,
  setKycBackImage = () => {},
  insuranceImagePath,
  setInsuranceImage,
  businessImagePath,
  setBusinessImage,
  corporateRegistryImagePath,
  setCorporateRegistryImage,
  identificationTypeOptions = [],
}) => {
  const { register, control, trigger, setValue } = useFormContext<PremiumStoreDocumentFormValues>();
  const selectedIdentificationType = useWatch({ control, name: 'identificationType' }) || '';
  const isPassportInsuranceType = decrypt(selectedIdentificationType) === INDENTIFICATION_TYPES.PASSPORT_INSURANCE;
  const { t } = useDynamicLocale();

  return (
    <div className="flex flex-col w-full space-y-4 pb-8 border-b-2 border-b-muted">
      <div className="flex gap-5 sm:gap-10 md:gap-15 lg:gap-20">
        <div className="min-w-[150.4px] w-[200px] xl:w-[302px] pl-12">
          <p className="text-base font-semibold whitespace-nowrap">
            <Translation id="storeManagement.premiumStores.manageStoreInformation.fieldTitle.documents" />
          </p>
        </div>
        <div className="w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 pb-4">
            <Select
              options={identificationTypeOptions}
              selected={identificationTypeFromApi}
              placeholder={t('common.form.placeholder.select')}
              label="storeManagement.premiumStores.documents.identificationType"
              {...register('identificationType')}
              forceRequired
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {isPassportInsuranceType ? (
              <React.Fragment key={'passport-insurance'}>
                {passportImagePath ? (
                  <ImagePreview
                    isRequired
                    documentImageClassName="h-[124px]"
                    variant="document"
                    imageSrc={passportImagePath}
                    alt="passportImage"
                    label="storeManagement.premiumStores.documents.uploadPassport"
                    cancelClickHandler={() => {
                      setPassportImage('');
                      setValue('passportImage', '');
                    }}
                  />
                ) : (
                  <Controller
                    name="passportImage"
                    control={control}
                    rules={{ required: 'Please upload an image' }}
                    render={({ field, fieldState }) => (
                      <Upload
                        isRequired
                        accept="image/*"
                        maxSizeMB={5}
                        multiple={false}
                        previewClassName="h-[124px] w-full p-0"
                        buttonText={t('common.page.button.browseFiles')}
                        error={fieldState.error?.message}
                        onFilesChange={(files) => {
                          field.onChange(files?.[0] ?? null);
                          void trigger('passportImage');
                        }}
                        label="storeManagement.premiumStores.documents.uploadPassport"
                        dropZoneClassName="relative w-full inline-flex items-center justify-center bg-surface-subtle  min-h-[124px]"
                      >
                        <div className="text-muted-foreground text-caption font-medium">
                          <Translation id="storeManagement.premiumStores.documents.imageNote" />
                        </div>
                      </Upload>
                    )}
                  />
                )}

                {insuranceImagePath ? (
                  <ImagePreview
                    isRequired
                    documentImageClassName="h-[124px]"
                    variant="document"
                    imageSrc={insuranceImagePath}
                    alt="insuranceImage"
                    label="storeManagement.premiumStores.documents.uploadInsuranceCard"
                    cancelClickHandler={() => {
                      setInsuranceImage('');
                      setValue('insuranceCardImage', '');
                    }}
                  />
                ) : (
                  <Controller
                    name="insuranceCardImage"
                    control={control}
                    rules={{ required: 'Please upload an image' }}
                    render={({ field, fieldState }) => (
                      <Upload
                        isRequired
                        accept="image/*"
                        maxSizeMB={5}
                        multiple={false}
                        buttonText={t('common.page.button.browseFiles')}
                        previewClassName="h-[124px] w-full p-0"
                        error={fieldState.error?.message}
                        onFilesChange={(files) => {
                          field.onChange(files?.[0] ?? null);
                          void trigger('insuranceCardImage');
                        }}
                        label="storeManagement.premiumStores.documents.uploadInsuranceCard"
                        dropZoneClassName="relative w-full inline-flex items-center justify-center bg-surface-subtle  min-h-[124px]"
                      />
                    )}
                  />
                )}
              </React.Fragment>
            ) : (
              <React.Fragment key="kyc-front-back">
                {kycFrontImage ? (
                  <ImagePreview
                    isRequired
                    documentImageClassName="h-[124px]"
                    variant="document"
                    imageSrc={kycFrontImage}
                    alt="passportImage"
                    label="storeManagement.premiumStores.documents.uploadKycFront"
                    cancelClickHandler={() => {
                      setKycFrontImage('');
                      setValue('kycFront', '');
                    }}
                  />
                ) : (
                  <Controller
                    name="kycFront"
                    control={control}
                    render={({ field, fieldState }) => (
                      <Upload
                        isRequired
                        accept="image/*"
                        maxSizeMB={5}
                        multiple={false}
                        previewClassName="h-[124px] w-full p-0"
                        buttonText={t('common.page.button.browseFiles')}
                        error={fieldState.error?.message}
                        onFilesChange={(files) => {
                          field.onChange(files?.[0] ?? null);
                          void trigger('kycFront');
                        }}
                        label="storeManagement.premiumStores.documents.uploadKycFront"
                        dropZoneClassName="relative w-full inline-flex items-center justify-center bg-surface-subtle min-h-[124px]"
                      >
                        <div className="text-muted-foreground text-caption font-medium">
                          <Translation id="storeManagement.premiumStores.documents.imageNote" />
                        </div>
                      </Upload>
                    )}
                  />
                )}
                {kycBackImage ? (
                  <ImagePreview
                    isRequired
                    documentImageClassName="h-[124px]"
                    variant="document"
                    imageSrc={kycBackImage}
                    alt="passportImage"
                    label="storeManagement.premiumStores.documents.uploadKycBack"
                    cancelClickHandler={() => {
                      setKycBackImage('');
                      setValue('kycBack', '');
                    }}
                  />
                ) : (
                  <Controller
                    name="kycBack"
                    control={control}
                    render={({ field, fieldState }) => (
                      <Upload
                        isRequired
                        maxSizeMB={5}
                        accept="image/*"
                        multiple={false}
                        previewClassName="h-[124px] w-full p-0"
                        error={fieldState.error?.message}
                        buttonText={t('common.page.button.browseFiles')}
                        onFilesChange={(files) => {
                          field.onChange(files?.[0] ?? null);
                          void trigger('kycBack');
                        }}
                        label="storeManagement.premiumStores.documents.uploadKycBack"
                        dropZoneClassName="relative w-full inline-flex items-center justify-center bg-surface-subtle min-h-[124px]"
                      />
                    )}
                  />
                )}
              </React.Fragment>
            )}

            {businessImagePath ? (
              <ImagePreview
                isRequired
                imageClassName="w-full"
                documentImageClassName="h-[124px]"
                variant="document"
                imageSrc={`${businessImagePath}`}
                alt="businessImage"
                label="storeManagement.premiumStores.documents.businessCertificate"
                cancelClickHandler={() => {
                  setBusinessImage('');
                  setValue('businessCertificateImage', '');
                }}
              />
            ) : (
              <Controller
                name="businessCertificateImage"
                control={control}
                render={({ field, fieldState }) => (
                  <Upload
                    accept="image/*"
                    multiple={false}
                    maxSizeMB={5}
                    isRequired
                    buttonText={t('common.page.button.browseFiles')}
                    previewClassName="h-[124px] w-full p-0"
                    error={fieldState.error?.message}
                    onFilesChange={(files) => {
                      field.onChange(files?.[0] ?? null);
                      void trigger('businessCertificateImage');
                    }}
                    label="storeManagement.premiumStores.documents.businessCertificate"
                    dropZoneClassName="relative w-full inline-flex items-center justify-center bg-surface-subtle min-h-[124px]"
                  >
                    <div className="text-muted-foreground text-caption font-medium">
                      <Translation id="storeManagement.premiumStores.documents.imageNote" />
                    </div>
                  </Upload>
                )}
              />
            )}

            {selectedBusinessType === BUSINESS_TYPES.CORPORATION && (
              <>
                {corporateRegistryImagePath ? (
                  <ImagePreview
                    isRequired
                    imageClassName="w-full"
                    documentImageClassName="h-[124px]"
                    variant="document"
                    imageSrc={`${corporateRegistryImagePath}`}
                    alt="corporateRegistryImage"
                    label="storeManagement.premiumStores.documents.corporateRegistry"
                    cancelClickHandler={() => {
                      setCorporateRegistryImage('');
                      setValue('corporateRegistryImage', '');
                    }}
                  />
                ) : (
                  <Controller
                    name="corporateRegistryImage"
                    control={control}
                    render={({ field, fieldState }) => (
                      <Upload
                        accept="image/*"
                        isRequired
                        maxSizeMB={5}
                        multiple={false}
                        buttonText={t('common.page.button.browseFiles')}
                        previewClassName="h-[124px] w-full p-0"
                        error={fieldState.error?.message}
                        onFilesChange={(files) => {
                          field.onChange(files?.[0] ?? '');
                          void trigger('corporateRegistryImage');
                        }}
                        label="storeManagement.premiumStores.documents.corporateRegistry"
                        dropZoneClassName="relative w-full inline-flex items-center justify-center bg-surface-subtle  min-h-[124px]"
                      />
                    )}
                  />
                )}
              </>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="storeManagement.premiumStores.documents.businessLicenseNumber"
              {...register('businessLicenseNumber')}
              placeholder={t('common.form.placeholder.businessLicenseNumber')}
              maxLength={13}
              type="text"
              inputMode="numeric"
              onlyNumbers
            />

            <DatePicker
              label="storeManagement.premiumStores.documents.licensedIssuedDate"
              placeholder="YYYY/MM/DD"
              name="licenseIssueDate"
              className="border-input"
              endMonth={new Date()}
              disableFuture={true}
              formDate={true}
              icon={<CalendarDropdownIcon className="text-foreground-subtle size-[20px]" />}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
